// src/api/client.ts
// Petit wrapper fetch avec injection du JWT depuis localStorage + parsing d'erreur uniforme.

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';
const TOKEN_KEY = 'nafola.token';

export const getToken = (): string | null => localStorage.getItem(TOKEN_KEY);
export const setToken = (token: string): void => localStorage.setItem(TOKEN_KEY, token);
export const clearToken = (): void => localStorage.removeItem(TOKEN_KEY);

export interface ApiErrorPayload {
  success?: false;
  message?: string;
  code?: string;
  errors?: unknown;
}

export class ApiError extends Error {
  public readonly status: number;
  public readonly code?: string;
  public readonly payload?: ApiErrorPayload;
  constructor(status: number, message: string, payload?: ApiErrorPayload) {
    super(message);
    this.status = status;
    this.code = payload?.code;
    this.payload = payload;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | undefined | null>;
  signal?: AbortSignal;
  // Si false, n'injecte pas le token (utile pour /auth/login et /auth/register)
  auth?: boolean;
}

const buildUrl = (path: string, query?: RequestOptions['query']): string => {
  const url = new URL(`${BASE_URL}${path}`, window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === '') continue;
      url.searchParams.set(k, String(v));
    }
  }
  // On garde le path + query relatif si same-origin (utile pour le proxy Vite)
  return url.pathname + url.search;
};

export const apiFetch = async <T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> => {
  const { method = 'GET', body, query, signal, auth = true } = opts;
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (auth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(buildUrl(path, query), {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal
  });

  const contentType = res.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');
  const payload = isJson ? await res.json().catch(() => ({})) : await res.text();

  if (!res.ok) {
    const jsonPayload = isJson ? (payload as ApiErrorPayload & { error?: string }) : undefined;
    const message =
      (jsonPayload?.message && String(jsonPayload.message).trim()) ||
      (jsonPayload?.error && String(jsonPayload.error).trim()) ||
      (!isJson && typeof payload === 'string' && payload.trim().slice(0, 200)) ||
      `Erreur HTTP ${res.status}`;
    if (res.status === 401) {
      clearToken();
    }
    throw new ApiError(res.status, message, isJson ? (payload as ApiErrorPayload) : undefined);
  }

  return payload as T;
};

interface UploadOptions {
  method?: 'POST' | 'PUT' | 'PATCH';
  form: FormData;
  query?: Record<string, string | number | undefined | null>;
  signal?: AbortSignal;
  auth?: boolean;
}

/**
 * Upload multipart/form-data (ex: KYC).
 * - Injecte Authorization si auth=true
 * - Ne set pas Content-Type (le browser gère boundary)
 */
export const apiUpload = async <T = unknown>(path: string, opts: UploadOptions): Promise<T> => {
  const { method = 'POST', form, query, signal, auth = true } = opts;
  const headers: Record<string, string> = {};
  if (auth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(buildUrl(path, query), {
    method,
    headers,
    body: form,
    signal
  });

  const contentType = res.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');
  const payload = isJson ? await res.json().catch(() => ({})) : await res.text();

  if (!res.ok) {
    const jsonPayload = isJson ? (payload as ApiErrorPayload & { error?: string }) : undefined;
    const message =
      (jsonPayload?.message && String(jsonPayload.message).trim()) ||
      (jsonPayload?.error && String(jsonPayload.error).trim()) ||
      (!isJson && typeof payload === 'string' && payload.trim().slice(0, 200)) ||
      `Erreur HTTP ${res.status}`;
    if (res.status === 401) {
      clearToken();
    }
    throw new ApiError(res.status, message, isJson ? (payload as ApiErrorPayload) : undefined);
  }

  return payload as T;
};
